import { Injectable, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { FullStory, init as initFullstory } from '@fullstory/browser';
import { Subscription } from 'rxjs';

import { AppEnvironment } from 'app/app-config';
import { LegacyApiUsersResponse, LegacyUsersApiService } from 'app/home/common/services/legacy/users/users.service';
import { GoogleTagManagerService } from 'app/shared/services/analytics/google-tagmanager/gtm.service';

/**
 * This purpose of this service is to interact with Fullstory (https://fullstory.com), our session tracking/recording integration.
 * Please ask VP of Product or an Engineering Manager for login credentials.
 * These credentials might also be in the company password manager (1Password).
 * Fullstory API Docs: https://developer.fullstory.com/introduction.
 */
@Injectable({
  providedIn: 'root',
})
export class FullstoryService implements OnDestroy {
  router = inject(Router);
  gtmService = inject(GoogleTagManagerService);
  usersService = inject(LegacyUsersApiService);

  /** The v2 operation API, equivalent to the global `FS` object. Overridable so tests never reach the real SDK. */
  public fs: typeof FullStory = FullStory;
  /** In v2 `init` is a separate export rather than a method on the API object. Overridable for the same reason. */
  public init: typeof initFullstory = initFullstory;
  public isFullstoryLoaded = false;
  public routerSubscription = new Subscription();
  public userSubscription = new Subscription();

  public initialize(env: AppEnvironment, fullstoryOrgID: string, guid: string) {
    if (this.isFullstoryLoaded) {
      return;
    }

    // Only initialize Fullstory on production. The ready callback fires once the session has
    // started, which is when the session URL becomes available.
    // https://developer.fullstory.com/browser/v2/get-session-details/
    this.init(
      {
        orgId: fullstoryOrgID,
        devMode: env !== 'production',
      },
      ({ sessionUrl }) => {
        sessionStorage.setItem('fs_url', sessionUrl);
      },
    );

    this.isFullstoryLoaded = true;

    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (sessionStorage.getItem('user_exists')) {
          this.userSubscription = this.usersService.getUserFromApi().subscribe((resp: LegacyApiUsersResponse) => {
            if (resp.kin_id) {
              this.sendDataToFullStory(resp, guid);
              this.userSubscription.unsubscribe();
              sessionStorage.setItem('fs_user', JSON.stringify(resp));
              this.gtmService.setKinId(resp.kin_id);
            }

            if (resp.kin_person_id) {
              this.gtmService.setKinPersonId(resp.kin_person_id);
            }

            if (resp.kin_segment_user_id) {
              this.gtmService.setKinSegmentId(resp.kin_segment_user_id);
            }
          });
        }
      }
    });
  }

  public sendDataToFullStory(data: LegacyApiUsersResponse, guid: string) {
    this.fs('setIdentity', {
      uid: data.kin_id.toString(),
      properties: {
        displayName: data?.name,
        email: data?.email,
      },
    });

    this.fs('setProperties', {
      type: 'user',
      properties: {
        uuid: guid,
        address: data?.address,
        state: data?.state,
        product: data?.product,
        property_id: data?.property_id,
      },
    });
  }

  public trackCustomEvent(eventName: string, properties: Record<string, unknown> = {}) {
    if (!this.isFullstoryLoaded) {
      return;
    }

    this.fs('trackEvent', { name: eventName, properties });
  }

  ngOnDestroy() {
    this.routerSubscription.unsubscribe();
    this.userSubscription.unsubscribe();
  }
}
